import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

const TableTagFilterDropdown = ({
  filterKey,
  dropdownRef,
  selectedValues = [],
  options = [],
  onSortChange = () => {},
  onHide = () => {},
  onSearchKeyPress = () => {},
  onSearchChange = () => {},
  onSelectAllChange = () => {},
  onItemChange = () => {},
  onClear = () => {},
  onApply = () => {},
  searchValue = "",
  showSearch = true,
  showSelection = true,
  showActions = true,
  renderInPortal = false,
  getOptionLabel = (item) => item,
  isSelected = (selectedItems, item) => selectedItems.includes(item),
}) => {
  const { t } = useTranslation();
  const base_assets = process.env.REACT_APP_BASE_ASSETS_URL;
  const selectAllId = `select-all-${filterKey}`;
  const [isOpen, setIsOpen] = useState(false);
  const [portalStyle, setPortalStyle] = useState({});
  const triggerRef = useRef(null);
  const portalMenuRef = useRef(null);

  const closePortalMenu = () => {
    if (renderInPortal) setIsOpen(false);
  };

  const updatePortalPosition = () => {
    if (!triggerRef.current || !renderInPortal) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const menuWidth = 286;
    let left = rect.left;
    if (left + menuWidth + 8 > viewportWidth) {
      left = Math.max(8, viewportWidth - menuWidth - 8);
    }
    setPortalStyle({
      position: "fixed",
      top: rect.bottom + 6,
      left,
      width: menuWidth,
      zIndex: 3000,
    });
  };

  useEffect(() => {
    if (!renderInPortal || !isOpen) return undefined;
    updatePortalPosition();
    const handleReposition = () => updatePortalPosition();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [renderInPortal, isOpen]);

  useEffect(() => {
    if (!renderInPortal || !isOpen) return undefined;
    const handleOutsideClick = (event) => {
      const target = event.target;
      if (
        triggerRef.current?.contains(target) ||
        portalMenuRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [renderInPortal, isOpen]);

  const handleSortSelect = (value) => {
    onSortChange({ key: filterKey, value });
    onHide(filterKey);
    closePortalMenu();
  };

  const handleClearClick = () => {
    onClear();
    closePortalMenu();
  };

  const handleApplyClick = () => {
    onApply();
    closePortalMenu();
  };

  const menuContent = (
    <>
      <div className="asc-des-order">
        <div
          className="asc-des-order-single cursor-pointer"
          onClick={() => handleSortSelect(1)}
        >
          <img src={base_assets + "images/icons/arrow-sort-up.png"} alt="" />
          {t("Sort Ascending")}
        </div>
        <div
          onClick={() => handleSortSelect(-1)}
          className="asc-des-order-single cursor-pointer"
        >
          <img src={base_assets + "images/icons/arrow-sort-down.png"} alt="" />
          {t("Sort Descending")}
        </div>
      </div>
      {showSearch && (
        <div className="com-custom-table-tag-filter-menu-search">
          <div className="com-custom-table-tag-filter-menu-search-inner d-flex align-items-center">
            <img src={base_assets + "images/icons/search-icon.png"} alt="" />
            <input
              className="border-0"
              type="text"
              placeholder="Search"
              value={searchValue}
              onKeyPress={(e) => e.key === "Enter" && onSearchKeyPress(e, filterKey)}
              onChange={(e) => onSearchChange(e, filterKey)}
            />
          </div>
        </div>
      )}
      {showSelection && (
        <div className="com-custom-table-tag-filter-menu-select">
          <div className="com-custom-table-tag-filter-menu-select-bar d-flex align-items-center justify-content-between">
            <div>
              <input
                className="fill-com-check form-check-input d-inline-block me-2 cursor-pointer"
                type="checkbox"
                id={selectAllId}
                onChange={(e) => onSelectAllChange(e, filterKey)}
                checked={options.length > 0 && selectedValues.length === options.length}
              />
              <label
                className="form-check-label cursor-pointer fw-semibold"
                htmlFor={selectAllId}
              >
                {t("Select All")}
              </label>
            </div>
            <label>
              {selectedValues.length}/{options.length}{" "}
              {t("Selected")}
            </label>
          </div>
          <ul className="com-custom-table-tag-filter-menu-select-list list-unstyled mt-3 ps-4">
            {options.length
              ? options.map((item, index) => {
                  const optionId = `${index}${filterKey}`;
                  return (
                    <li
                      className="com-custom-table-tag-filter-menu-select-list-single"
                      key={optionId}
                    >
                      <input
                        className="fill-com-check form-check-input d-inline-block me-2 cursor-pointer"
                        type="checkbox"
                        id={optionId}
                        checked={isSelected(selectedValues, item)}
                        onChange={(e) => onItemChange(e, item, filterKey)}
                      />
                      <label
                        className="form-check-label cursor-pointer"
                        htmlFor={optionId}
                      >
                        {getOptionLabel(item)}
                      </label>
                    </li>
                  );
                })
              : ""}
          </ul>
        </div>
      )}
      {showActions && (
        <div className="com-custom-table-tag-filter-menu-btn-grp text-center d-flex align-items-center gap-2">
          <button
            className="com-custom-table-tag-filter-menu-btn-grp-single flex-grow-1 bg-transparent fw-semibold graythemecolor"
            onClick={handleClearClick}
          >
            {t("Clear")}
          </button>
          <button
            className={`com-custom-table-tag-filter-menu-btn-grp-single flex-grow-1 main-green-bg text-white fw-semibold ${selectedValues.length>0?'table-filter-apply':'table-filter-apply-disable'}`}
            onClick={selectedValues.length>0?()=>handleApplyClick():undefined}
          >
            {t("Apply")}
          </button>
        </div>
      )}
    </>
  );

  return (
    <div
      className={`${renderInPortal ? "" : "dropdown d-inline-block"} com-custom-table-tag-filter dtbs-filter-dropdown`}
    >
      <button
        ref={triggerRef}
        className={`after-none bg-transparent border-0 p-0 ${renderInPortal ? "" : "dropdown-toggle"}`}
        type="button"
        data-bs-toggle={renderInPortal ? undefined : "dropdown"}
        aria-expanded={renderInPortal ? isOpen : false}
        data-bs-auto-close={renderInPortal ? undefined : "outside"}
        onClick={
          renderInPortal
            ? () => {
                setIsOpen((prev) => !prev);
              }
            : undefined
        }
      >
        <img
          className="ms-2 com-custom-table-tag-filter-img"
          src={base_assets + "images/icons/table-filter-icon.png"}
          alt=""
        />
      </button>
      {renderInPortal ? (
        isOpen &&
        createPortal(
          <div
            className="dropdown-menu show com-custom-table-tag-filter-menu dtbs-filter-dropdown-menu dtbs-filter-portal-menu p-0 border-0 overflow-hidden"
            style={portalStyle}
            ref={portalMenuRef}
          >
            {menuContent}
          </div>,
          document.body,
        )
      ) : (
        <div
          className="dropdown-menu com-custom-table-tag-filter-menu dtbs-filter-dropdown-menu p-0 border-0 overflow-hidden"
          ref={dropdownRef}
        >
          {menuContent}
        </div>
      )}
    </div>
  );
};

export default TableTagFilterDropdown;
